import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  ICapitalMovementByIdReturnType,
  ICapitalMovementListByInvestorProfileArgs,
} from "api";
import {
  PwmProfileGetByIdDocument,
  PwmProfileListDocument,
  PwmProfileCreateDocument,
  PwmProfileUpdateDocument,
  PwmProfileListPaginatedDocument,
  PwmInvestorListDocument,
  PwmProfileGetInvestorDocument,
  PwmProfileDeleteDocument,
  CapitalMovementListByPwmProfileDocument,
} from "api/generated";
import { mapInvestorName, Optional, PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IPwmProfileModel,
  IPwmProfileCreateVars,
  IPwmProfilePaginatedResponse,
  IPwmProfilePaginatedVars,
  IPwmProfileUpdateVars,
  IPwmProfileListModel,
  IPwmInvestorListModel,
  IInvestorPwmProfileModel,
} from "./types";

export async function getInvestorsAvailableList(): Promise<
  IPwmInvestorListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmInvestorListDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmInvestorList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfile(
  investorId: number
): Promise<Optional<IPwmProfileModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileGetByIdDocument,
      variables: {
        investorId: investorId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileGetById;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileInvestor(
  investorId: number
): Promise<Optional<IInvestorPwmProfileModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileGetInvestorDocument,
      variables: {
        investorId: investorId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileGetInvestor;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfilePaginated(
  variables: IPwmProfilePaginatedVars
): Promise<Optional<IPwmProfilePaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileList(): Promise<IPwmProfileListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileListDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileList || [];
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovementListByPwmProfile(
  variables?: ICapitalMovementListByInvestorProfileArgs
): Promise<
  PartialNullable<
    Omit<
      ICapitalMovementByIdReturnType,
      "createdByName" | "updatedByName" | "switchByAmount"
    >
  >[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementListByPwmProfileDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return (res?.data?.capitalMovementListByPwmProfile ?? []).map((item) => ({
      ...item,
      investorProfile: {
        ...item.investorProfile,
        ...mapInvestorName(item.investorProfile),
      },
    }));
  } catch (error) {
    throw error;
  }
}

export async function createPwmProfile(
  variables: IPwmProfileCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict all PWM profile lists
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: [
        "pwmProfileListPaginated",
        "pwmProfileList",
        "pwmInvestorList",
      ],
    });

    return Boolean(res?.data?.pwmProfileCreate);
  } catch (error) {
    throw error;
  }
}

export async function updatePwmProfile(
  variables: IPwmProfileUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById queries
    executeEviction(client, {
      typename: "PwmProfile",
      id: variables.investorId.toString(),
      fieldNames: [
        "pwmProfileListPaginated",
        "pwmProfileList",
        "pwmProfileGetById",
        "pwmInvestorList",
        "capitalMovementListByPwmProfile",
      ],
    });

    return Boolean(res?.data?.pwmProfileUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deletePwmProfile(investorId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileDeleteDocument,
      variables: {
        investorId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + all lists
    executeEviction(client, {
      typename: "PwmProfile",
      id: investorId.toString(),
      fieldNames: [
        "pwmProfileListPaginated",
        "pwmProfileList",
        "pwmInvestorList",
      ],
    });

    return Boolean(res?.data?.pwmProfileDelete);
  } catch (error) {
    throw error;
  }
}
